/**
 * Copyright (c) Microsoft. All rights reserved.
 */

// tslint:disable:strict-boolean-expressions
// tslint:disable:promise-function-async

import { CorrelationVector, toJson } from './Common';
import {
    ConnectionInfo, Listener, LogProvider, Request, Response, SendResponseResult, SkypeTokenProvider, TrouterState,
    UserActivityState
} from './Interfaces';
import { IMessageLossIndicator, ITrouterOptions, ITrouterRequest, ITrouterResponse, ServerState } from './InternalInterfaces';
import { Logger } from './Logger';
import { IManagerConsumer, ReconnectReason, TrouterConnection } from './TrouterConnection';
import { IManagerWorker, TrouterManagerFsm } from './TrouterManagerFsm';


/**
 * UserActivity object
 */
export class UserActivityObject {
    public readonly correlationVector: CorrelationVector;

    constructor(readonly state: UserActivityState, correlationVector?: CorrelationVector) {
        this.correlationVector = correlationVector !== undefined ? correlationVector : CorrelationVector.extend();
    }

    public getStateString() {
        switch (this.state) {
            case UserActivityState.Active:   return 'active';
            case UserActivityState.Inactive: return 'inactive';
            case UserActivityState.Unknown:  return 'unknown';
            default:                         return 'undefined';
        }
    }

    public toEventObject() {
        return { state: this.getStateString(), cv: this.correlationVector.value() };
    }

    public toEventJSON() {
        return toJson(this.toEventObject());
    }
}

/**
 * Trouter implementation
 */
export class TrouterManager implements IManagerWorker, IManagerConsumer {
    private logger: Logger;
    private fsm: TrouterManagerFsm;
    private firstConnection?: TrouterConnection;
    private secondConnection?: TrouterConnection;
    /** Value of first connection before stop() - to dispatch onDisconnected() of first connection */
    private storedFirstConnection?: TrouterConnection;
    private baseEndpointUrl: string;
    private connectionCache?: {};

    /** Cache for processed message loss to catch duplicates and to not pass them to the listeners */
    private processedMessageLoss: object;
    private userActivityObject: UserActivityObject;

    public constructor(
        private logFunc: LogProvider,
        private options: ITrouterOptions,
        private tokenProvider: SkypeTokenProvider,
        private listener: Listener
    ) {
        this.logger = new Logger('Manager', logFunc);
        this.logger.info(`Created TrouterManager with options ${toJson(this.options)}`);
        this.fsm = new TrouterManagerFsm(logFunc, this);
        this.baseEndpointUrl = '';
        this.processedMessageLoss = {};
        this.userActivityObject = new UserActivityObject(UserActivityState.Unknown);
    }

    public start() {
        this.fsm.start();
    }

    public stop(dontUnregister?: boolean) {
        this.fsm.stop(dontUnregister);
    }

    public configure(options: ITrouterOptions) {
        this.options = options;
        if (this.firstConnection !== undefined) {
            this.firstConnection.configure(options);
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.configure(options);
        }
        this.logger.info(`Reconfigured TrouterManager with options ${toJson(this.options)}`);
    }

    public checkConnection(disconnectDetected: boolean) {
        if (this.firstConnection !== undefined) {
            this.firstConnection.checkConnection(disconnectDetected);
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.checkConnection(disconnectDetected);
        }
    }

    public async resendRegistration(): Promise<void> {
        if (this.secondConnection !== undefined) {
            this.logger.info('Resending registration on the second/new connection');

            return this.secondConnection.resendRegistration();
        }

        if (this.firstConnection !== undefined) {
            this.logger.info('Resending registration on the first/current connection');

            return this.firstConnection.resendRegistration();
        }

        this.logger.info('No connection to resend registration on, will be done upon (re)connect');
    }

    public getServerState(): ServerState | undefined {
        if (this.firstConnection !== undefined) {
            return this.firstConnection.getServerState();
        }

        return undefined;
    }

    /** Returns current state */
    public getState(): TrouterState {
        return this.fsm.getState();
    }

    //////////// IManagerWorker ////////////
    public startFirstConnection() {
        const connection = new TrouterConnection(this.logFunc, this.options, this, this.tokenProvider, this.userActivityObject);
        this.firstConnection = connection;

        this.getConnectionCache()
            .then(connectionCache => {
                connection.start(connectionCache);
            })
            .catch();
    }

    public startSecondConnection(useConnectParamsFromCache: boolean) {
        const connection = new TrouterConnection(this.logFunc, this.options, this, this.tokenProvider, this.userActivityObject);
        this.secondConnection = connection;
        if (this.firstConnection !== undefined) {
            this.firstConnection.disableRegistrationsAndAutoReconnect();
        }

        if (useConnectParamsFromCache) {
            this.getConnectionCache()
                .then(connectionCache => {
                    connection.start(connectionCache);
                })
                .catch();
        } else {
            connection.start();
        }
    }

    public stopFirstConnection(dontUnregister: boolean) {
        if (this.firstConnection !== undefined) {
            // Store first connection to be able to compare in onDisconnected() handler
            this.storedFirstConnection = this.firstConnection;
            this.firstConnection.stop(dontUnregister);
            this.firstConnection = undefined;
        }
    }

    public stopSecondConnection(dontUnregister: boolean) {
        if (this.secondConnection !== undefined) {
            this.secondConnection.stop(dontUnregister);
            this.secondConnection = undefined;
        }
    }

    public stopSecondConnectionDelayed() {
        if (this.secondConnection !== undefined) {
            const connection = this.secondConnection;
            this.secondConnection = undefined;
            this.logger.info(`Closing an inactive connection in ${Math.round(this.options.lingeringConnectionDelayMs / 1000)}s`);
            setTimeout(() => {
                connection.stop(true);
            }, this.options.lingeringConnectionDelayMs);
        }
    }

    public forceStopLingeringConnection() {
        if (this.storedFirstConnection) {
            this.storedFirstConnection.stop(false);
            this.storedFirstConnection = undefined;
        }
    }

    public switchConnections() {
        const tmp = this.firstConnection;
        this.firstConnection = this.secondConnection;
        this.secondConnection = tmp;
    }

    public doesSecondConnectionExist(): boolean {
        return this.secondConnection !== undefined;
    }

    public dispatchConnected() {
        if (this.firstConnection !== undefined) {
            const ss = this.firstConnection.getServerState();
            const baseEndpointUrl = (ss.url.slice(-1) === '/') ? ss.url.slice(0, -1) : ss.url;
            const connectionInfo: ConnectionInfo = {
                baseEndpointUrl: baseEndpointUrl,
                newEndpointUrl: (baseEndpointUrl !== this.baseEndpointUrl),
                c2cUrlBase: ss.c2cUrlBase,
                clientId: ss.connectedClientId,
                connectionId: ss.connectionId,
                connectionTtlSec: ss.getRemainingTtlInSec()
            };
            this.baseEndpointUrl = baseEndpointUrl;
            this.listener.onTrouterConnected(ss.url, connectionInfo);
        }
    }

    public dispatchReconnecting() {
        if (this.listener.onTrouterDisconnected) {
            this.listener.onTrouterDisconnected();
        }
    }

    public dispatchStopped() {
        if (this.listener.onTrouterDisconnected) {
            this.listener.onTrouterDisconnected();
        }
    }

    public dispatchRegistrationState(isRegistered: boolean) {
        if (this.options.registrationStateCallback) {
            this.options.registrationStateCallback(isRegistered);
        }
    }

    //////////// IManagerConsumer ////////////
    public onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse) {
        const req: Request = {
            id: request.id,
            method: request.method,
            path: `/${request.shortUrl}`,
            body: request.body,
            headers: request.headers
        };
        const rsp: Response = {
            id: request.id,
            status: 0,
            headers: {},
            body: '',
            send: () => {
                if (rsp.status <= 100 || rsp.status >= 999) {

                    return SendResponseResult.Incomplete;
                }
                response.writeHead(rsp.status, rsp.headers);

                return response.end(rsp.body);
            }
        };
        this.listener.onTrouterRequest(req, rsp);
    }

    public onConnected(connection: TrouterConnection) {
        this.fsm.onConnected(connection === this.firstConnection);
    }

    public onRegistered(connection: TrouterConnection) {
        this.fsm.onRegistered(connection === this.firstConnection);
    }

    public onUnregistered(connection: TrouterConnection) {
        this.fsm.onUnregistered(connection === this.firstConnection || connection === this.storedFirstConnection);
    }

    public onReconnecting(connection: TrouterConnection) {
        this.fsm.onReconnecting(connection === this.firstConnection);
    }

    public onReconnectIsRequired(connection: TrouterConnection, useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        this.fsm.onReconnectionRequired(connection === this.firstConnection, useConnectParamsFromCache, reason);
    }

    public onDisconnected(connection: TrouterConnection) {
        this.fsm.onDisconnected(connection === this.storedFirstConnection);
        this.storedFirstConnection = undefined;
    }

    public onUserActivityStateAccepted(cv: string) {
        if (this.listener.onTrouterUserActivityStateAccepted) {
            this.listener.onTrouterUserActivityStateAccepted(cv);
        }
    }

    public onConnectionParametersUpdated(connectionParameters: {}) {
        this.setConnectionCache(connectionParameters);
    }

    public setUserActivityState(state: UserActivityState, correlationVector?: string): void {
        this.userActivityObject = new UserActivityObject(state, CorrelationVector.extend(correlationVector));
        if (this.secondConnection !== undefined) {
            this.logger.info(`Setting user activity ${this.userActivityObject.toEventJSON()} on the second/new connection`);
            this.secondConnection.setUserActivityState(this.userActivityObject);

            return;
        }

        if (this.firstConnection !== undefined) {
            this.logger.info(`Setting user activity ${this.userActivityObject.toEventJSON()} on the first/current connection`);
            this.firstConnection.setUserActivityState(this.userActivityObject);

            return;
        }
    }

    public onTrouterMessageLost(indicators: IMessageLossIndicator[]) {
        if (this.listener.onTrouterMessageLoss) {
            if (indicators && indicators.length) {
                // Filter out already seen dropped indicators and inform server that they are processed
                const duplicates = indicators.filter(di => this.processedMessageLoss[`${di.tag}-${di.etag}`] !== undefined);
                if (duplicates.length) {
                    this.logger.warn('onTrouterMessageLoss - removing duplicates and sending event to server');
                    this.sendProcessedDroppedIndicators(duplicates);
                    indicators = indicators.filter(di => this.processedMessageLoss[`${di.tag}-${di.etag}`] === undefined);

                    if (!indicators.length) {
                        this.logger.warn('onTrouterMessageLoss - All the data are duplicated');

                        return;
                    }
                }

                // Send the data to the listeners
                const messageLossProcessed = this.listener.onTrouterMessageLoss(indicators.map(di => di.tag));
                if (!messageLossProcessed) {
                    this.logger.warn('onTrouterMessageLoss - flow tags have not been processed by listeners');

                    return;
                }

                // Update the cache
                indicators.forEach(di => {
                    this.processedMessageLoss[`${di.tag}-${di.etag}`] = '';
                });
                this.sendProcessedDroppedIndicators(indicators);
            } else {
                this.logger.warn('onTrouterMessageLoss - no flow tags have been provided');
            }
        }
    }

    private getConnectionCache(): Promise<{} | undefined> {
        if (!this.options.connectionCache) {

            return Promise.resolve(this.connectionCache);
        }

        this.logger.debug('Querying host\'s connection cache');

        return this.options.connectionCache.onGetTrouterConnectionCache()
            .then((serialized) => {

                return serialized ? JSON.parse(serialized) : undefined;
            })
            .catch((e) => {
                this.logger.warn(`Invalid connection cache content provided: ${e}`);

                return this.connectionCache;
            });
    }

    private setConnectionCache(content: {}) {
        this.connectionCache = content;
        if (this.options.connectionCache) {
            try {
                this.options.connectionCache.onSetTrouterConnectionCache(JSON.stringify(content));
            } catch (e) {
                this.logger.warn(`Error setting external connection cache: ${e}`);
            }
        }
    }

    private sendProcessedDroppedIndicators(messageLossIndicators: IMessageLossIndicator[]) {
        if (this.firstConnection !== undefined) {
            this.firstConnection.sendProcessedDroppedIndicators(messageLossIndicators);

            return;
        }

        if (this.secondConnection !== undefined) {
            this.secondConnection.sendProcessedDroppedIndicators(messageLossIndicators);

            return;
        }
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterManager.ts